import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyInfo } from '../mock';
import Header from '../components/Header';

const TermsOfServicePage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Terms of Service | SGS Global Media';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Terms of Service for SGS Global Media. Read our terms governing the use of our website and services.');
  }, []);

  const sections = [
    {
      title: '1. Agreement to Terms',
      content: `By accessing or using the website at sgsglobalmedia.com and engaging our services, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our website or services.

These terms apply to all visitors, users, and clients of SGS Global Media.`,
    },
    {
      title: '2. Services',
      content: `SGS Global Media provides digital marketing services including but not limited to:
• AI-powered lead generation
• Sales funnel design and optimisation
• CRM implementation and AI automation
• Web development and conversion optimisation

The specific scope, deliverables, timelines, and fees for any engagement are defined in a separate Statement of Work (SOW) or Service Agreement signed by both parties. These Terms of Service govern the general relationship between SGS Global Media and clients.`,
    },
    {
      title: '3. Client Responsibilities',
      content: `To enable us to deliver our services effectively, clients agree to:
• Provide accurate, complete, and timely information, materials, and approvals required for the engagement
• Designate a primary point of contact with authority to approve deliverables
• Provide access to required platforms, accounts, and tools within the agreed timeframe
• Pay invoices on time as specified in the Service Agreement
• Comply with the terms of service of any third-party platforms used in the engagement (Meta, Google, LinkedIn, etc.)

Delays caused by client inaction or late provision of materials may result in timeline adjustments and additional charges.`,
    },
    {
      title: '4. Payment Terms',
      content: `Specific payment terms are outlined in the Service Agreement for each engagement. General terms:

• Invoices are due within 14 days of the invoice date unless otherwise agreed in writing
• A deposit (typically 50% of the project fee) is required before work commences
• Late payments may incur interest at the rate of 2% per month on the outstanding balance
• SGS Global Media reserves the right to pause or terminate services if payment is not received within 30 days of the due date
• All fees are exclusive of applicable taxes (GST, VAT, etc.) unless stated otherwise`,
    },
    {
      title: '5. Intellectual Property',
      content: `Upon receipt of full payment for the engagement:
• All deliverables created specifically for the client (website code, ad creative, copywriting, funnel assets) become the property of the client
• SGS Global Media retains ownership of pre-existing tools, templates, frameworks, and methodologies used in delivering the services
• SGS Global Media retains the right to reference the engagement as part of its portfolio and case studies, unless the client requests confidentiality in writing

Client-provided materials (logos, brand assets, copy) remain the property of the client. The client warrants that they have the rights to all materials provided to SGS Global Media.`,
    },
    {
      title: '6. Confidentiality',
      content: `Both parties agree to keep confidential any proprietary or sensitive information disclosed during the engagement, including business strategies, client data, pricing, and technical methodologies.

This obligation of confidentiality does not apply to information that:
• Is or becomes publicly available through no breach of this agreement
• Was already known to the receiving party prior to disclosure
• Is required to be disclosed by law or regulatory authority`,
    },
    {
      title: '7. Results and Guarantees',
      content: `SGS Global Media applies proven methodologies and best practices to all engagements. However, digital marketing results are influenced by factors outside our control including market conditions, platform algorithm changes, client offer quality, and competitive landscape.

Unless explicitly stated in a signed Service Agreement, we do not guarantee specific results such as a defined number of leads, revenue targets, or ranking positions.

Where a results guarantee is offered in a Service Agreement, the specific terms, conditions, and remedy for non-performance are defined in that document.`,
    },
    {
      title: '8. Limitation of Liability',
      content: `To the maximum extent permitted by applicable law, SGS Global Media shall not be liable for:
• Indirect, incidental, special, consequential, or punitive damages
• Loss of profits, revenue, data, or business opportunities
• Damages arising from third-party platform changes, outages, or policy updates (including Meta, Google, LinkedIn)

Our total liability for any claim arising from or related to our services shall not exceed the total fees paid by the client in the 3 months preceding the claim.`,
    },
    {
      title: '9. Termination',
      content: `Either party may terminate an engagement by providing written notice as specified in the Service Agreement (typically 30 days).

Upon termination:
• The client is responsible for all fees due for work completed up to the termination date
• SGS Global Media will provide the client with all completed deliverables and transfer access to accounts managed on the client's behalf
• Any prepaid fees for future services will be refunded on a pro-rata basis, minus work already completed

SGS Global Media reserves the right to terminate immediately and without notice if the client breaches these terms or engages in unlawful activity.`,
    },
    {
      title: '10. Governing Law',
      content: `These Terms of Service are governed by the laws of India. Any disputes arising from these terms or our services shall be subject to the exclusive jurisdiction of the courts of Pune, Maharashtra, India.

For clients in the United Kingdom or European Union, nothing in these terms affects your statutory rights as a consumer or business under applicable local law.`,
    },
    {
      title: '11. Changes to These Terms',
      content: `We reserve the right to update these Terms of Service at any time. Changes will be effective upon posting to our website. We will update the "Last updated" date at the top of this page.

Continued use of our website or services after changes are posted constitutes acceptance of the updated terms.`,
    },
    {
      title: '12. Contact',
      content: `For questions about these Terms of Service, please contact:

SGS Global Media
Balewadi High Street, Pune, Maharashtra 411045, India
Email: ${companyInfo.email}`,
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#020617', color: '#94a3b8', fontFamily: "'DM Sans', sans-serif" }}>

      <Header
        onBooking={() => { navigate('/'); setTimeout(() => { const el = document.querySelector('#contact'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 100); }}
        onSectionClick={(id) => { navigate('/'); setTimeout(() => { if (id && id !== 'home') { const el = document.querySelector(id); if (el) el.scrollIntoView({ behavior: 'smooth' }); } else { window.scrollTo({ top: 0, behavior: 'smooth' }); } }, 100); }}
      />

      {/* Hero */}
      <header className="pt-48 pb-16 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <nav aria-label="Breadcrumb" className="flex justify-center gap-2 text-xs text-gray-500 mb-6">
            <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }} className="hover:text-[#007bff] transition-colors">Home</a>
            <span>/</span>
            <span className="text-[#007bff]">Terms of Service</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
            Terms of Service
          </h1>
          <p className="text-gray-500 text-sm">Last updated: March 15, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div
            className="rounded-2xl p-8 md:p-12 mb-8"
            style={{ background: '#111620', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <p className="text-gray-400 leading-relaxed">
              These Terms of Service govern your use of the SGS Global Media website and the services we provide. Please read them carefully before engaging our services. By using our website or signing a Service Agreement with us, you agree to these terms.
            </p>
          </div>

          <div className="space-y-6">
            {sections.map((section, i) => (
              <div
                key={i}
                className="rounded-2xl p-8 md:p-10"
                style={{ background: '#111620', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <h2 className="text-white font-bold text-lg mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>
                  {section.title}
                </h2>
                <p className="text-gray-400 text-sm leading-[1.9] whitespace-pre-line">{section.content}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-10 px-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-sm font-extrabold" style={{ color: 'rgba(255,255,255,0.4)' }}>{companyInfo.name}</span>
          <nav aria-label="Legal links" className="flex gap-6">
            <a href="/privacy-policy" onClick={e => { e.preventDefault(); navigate('/privacy-policy'); }} className="text-xs text-gray-600 hover:text-[#007bff] transition-colors uppercase tracking-[0.15em]">Privacy Policy</a>
            <a href="/terms-of-service" className="text-xs text-[#007bff] uppercase tracking-[0.15em]">Terms of Service</a>
          </nav>
          <p className="text-gray-600 text-xs uppercase tracking-[0.2em]">© 2025 SGS Global Media</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
